// Per-day FLOAT-CRNA overlay (hospitalId -> array of CRNA employeeIds, one entry per float slot), local to
// the Board. A site's float count comes from the Simulate tab (Hospital.floatCrnaSlots); this overlay records
// WHO the runner has pre-assigned into each of those slots. A float CRNA belongs to the SITE, not a room, so
// this is keyed by hospitalId (not scheduleId/roomId). Reversible — clearing a slot removes the entry.
// Stored per day (survives refresh), exactly like the site locks and room transfers. Never leaves the machine.
export const floatCrnaKey = (date: string) => `oc-float-crna-${date}`;

// Map of hospitalId -> (employeeId | null)[]. Index i is float slot i for that site; null = empty slot.
export type FloatCrnaMap = Record<string, (string | null)[]>;

export function loadFloatCrnas(date: string): FloatCrnaMap {
  try {
    const raw = localStorage.getItem(floatCrnaKey(date));
    // Accept only a plain object map of arrays; reject a JSON array (typeof [] === 'object') or any other
    // shape so a corrupt entry falls back to an empty overlay instead of poisoning float lookups.
    if (raw) {
      const v = JSON.parse(raw);
      if (v && typeof v === 'object' && !Array.isArray(v)) {
        const out: FloatCrnaMap = {};
        for (const k of Object.keys(v)) if (Array.isArray(v[k])) out[k] = v[k];
        return out;
      }
    }
  } catch { /* ignore */ }
  return {};
}

// The CRNA in float slot `index` at `hospitalId`, or undefined if empty/unset.
export function floatCrnaAt(map: FloatCrnaMap, hospitalId: string, index: number): string | undefined {
  const arr = map[hospitalId];
  const v = arr ? arr[index] : undefined;
  return v == null ? undefined : v;
}

// All CRNA employeeIds currently sitting in any float slot — used to drop them from the bullpen ("used").
export function floatCrnaEmployeeIds(map: FloatCrnaMap): Set<string> {
  const ids = new Set<string>();
  for (const arr of Object.values(map)) for (const id of arr) if (id) ids.add(id);
  return ids;
}

// Reverse of the overlay: employeeId -> the set of hospitalIds where they sit in a float slot. A float CRNA
// belongs to that SITE (not a room) and reports at 06:00, so the CRNA tab uses this to list them as
// assigned-to-float at that site rather than dropping them into the "Unassigned" group.
export function floatSitesByEmployee(map: FloatCrnaMap): Map<string, Set<string>> {
  const out = new Map<string, Set<string>>();
  for (const [hospitalId, arr] of Object.entries(map)) {
    for (const id of arr) {
      if (!id) continue;
      if (!out.has(id)) out.set(id, new Set());
      out.get(id)!.add(hospitalId);
    }
  }
  return out;
}

// Assign (employeeId) or clear (null) one float slot; returns the updated map. Pads the site's slot array
// with nulls up to `index` so a mid-array assignment never creates a sparse array.
export function setFloatCrna(date: string, hospitalId: string, index: number, employeeId: string | null): FloatCrnaMap {
  const map = loadFloatCrnas(date);
  const arr = (map[hospitalId] || []).slice();
  while (arr.length <= index) arr.push(null);
  arr[index] = employeeId;
  map[hospitalId] = arr;
  try { localStorage.setItem(floatCrnaKey(date), JSON.stringify(map)); } catch { /* ignore */ }
  return map;
}

// Write the algorithm's float picks for ONE site into the overlay (Phase 2). The runner's pre-assigned CRNAs
// were fed to the algorithm and come back inside `crnaIds`, so we keep any already-placed CRNA in its current
// slot and fill the remaining EMPTY slots with the rest, in order. The slot array is sized to hold them all
// (the board renders `floatCrnaSlots` boxes; extra picks beyond that simply won't show). Returns updated map.
export function applyFloatAssignments(date: string, hospitalId: string, crnaIds: string[]): FloatCrnaMap {
  const map = loadFloatCrnas(date);
  const arr = (map[hospitalId] || []).slice();
  while (arr.length < crnaIds.length) arr.push(null);
  const already = new Set(arr.filter((x): x is string => !!x));
  const toPlace = crnaIds.filter(id => !already.has(id));
  for (let i = 0; i < arr.length && toPlace.length; i++) if (!arr[i]) arr[i] = toPlace.shift()!;
  map[hospitalId] = arr;
  try { localStorage.setItem(floatCrnaKey(date), JSON.stringify(map)); } catch { /* ignore */ }
  return map;
}

// Remove a CRNA from EVERY float slot at every site (used when they're placed into a real room instead, so a
// person is never double-counted as both floating and in a room). Returns the updated map.
export function clearFloatCrnaForEmployee(date: string, employeeId: string): FloatCrnaMap {
  const map = loadFloatCrnas(date);
  let changed = false;
  for (const k of Object.keys(map)) {
    const arr = map[k];
    for (let i = 0; i < arr.length; i++) if (arr[i] === employeeId) { arr[i] = null; changed = true; }
  }
  if (changed) { try { localStorage.setItem(floatCrnaKey(date), JSON.stringify(map)); } catch { /* ignore */ } }
  return map;
}

// Clear EVERY float-CRNA assignment for a date. Used when a new simulation replaces the day, so floats the
// runner pre-assigned in the previous sim don't linger on the fresh board (this overlay is keyed by the stable
// hospitalId, so it would otherwise carry straight over).
export function clearFloatCrnas(date: string): void {
  try { localStorage.removeItem(floatCrnaKey(date)); } catch { /* ignore */ }
}

// ===========================================================================
// Per-day FLOAT-CRNA SLOT COUNT overlay (hospitalId -> number of float slots), local to the Board. This is the
// COUNT of empty float boxes a site shows; the NAMES map above records who sits in each. It is stored PER DAY
// (each day's Simulate run writes its own), so a float set on one day NEVER bleeds onto another. This replaces
// the old GLOBAL Hospital.floatCrnaSlots field, which had no date and so let the last-simulated day's float
// config govern every other day (the "why are floats showing up on days I set to zero" bug).
// ===========================================================================
export const floatSlotsKey = (date: string) => `oc-float-slots-${date}`;
// Map of hospitalId -> slot count (positive integers only; a site with no floats simply has no entry).
export type FloatSlotMap = Record<string, number>;

export function loadFloatSlots(date: string): FloatSlotMap {
  try {
    const raw = localStorage.getItem(floatSlotsKey(date));
    // Accept only a plain object map (reject a JSON array or other shape) so a corrupt entry falls back to "no
    // floats" instead of poisoning slot lookups.
    if (raw) {
      const v = JSON.parse(raw);
      if (v && typeof v === 'object' && !Array.isArray(v)) {
        const out: FloatSlotMap = {};
        for (const k of Object.keys(v)) { const n = Math.floor(Number(v[k])); if (Number.isFinite(n) && n > 0) out[k] = n; }
        return out;
      }
    }
  } catch { /* ignore */ }
  return {};
}

// Replace a day's float-slot config wholesale (called when a simulation sets the day up). Only POSITIVE counts
// are stored, so a site the sim left at 0 has no entry — re-simulating a day with no floats clears it clean.
export function saveFloatSlots(date: string, counts: Record<string, number>): void {
  const out: FloatSlotMap = {};
  for (const k of Object.keys(counts || {})) { const n = Math.floor(Number(counts[k])); if (Number.isFinite(n) && n > 0) out[k] = n; }
  try { localStorage.setItem(floatSlotsKey(date), JSON.stringify(out)); } catch { /* ignore */ }
}

// The configured float-slot count at a site for the day (0 if unset).
export function floatSlotsAt(map: FloatSlotMap, hospitalId: string): number {
  return Math.max(0, Math.floor(map[hospitalId] || 0));
}

// Clear a day's float-slot config entirely.
export function clearFloatSlots(date: string): void {
  try { localStorage.removeItem(floatSlotsKey(date)); } catch { /* ignore */ }
}
